import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { api, money } from "@/lib/api";
import { RewardPopup } from "@/components/RewardPopup";
import { BedDouble, Bath, Car, CheckCircle2, MapPin } from "lucide-react";

const INVESTOR_OFFER_1 = { name: "Tax Depreciation Schedule", value: 700 };

export default function InvestorFloorplan() {
  const { session, data, next, back, mergeData, addReward } = useJourney();
  const [plans, setPlans] = useState([]);
  const [selectedId, setSelectedId] = useState(data.investor_floorplan?.id || null);
  const [landPrice, setLandPrice] = useState(data.investor_floorplan?.land_price ?? "");
  const [showReward, setShowReward] = useState(false);

  useEffect(() => {
    api.floorplans().then((list) => {
      const inv = (list || []).filter((p) => (p.beds || 0) >= 3);
      setPlans(inv.length ? inv : (list || []));
    }).catch(() => toast.error("Could not load floor plans."));
  }, []);

  const loc = data.investor_location || {};
  const selectedPlan = useMemo(() => plans.find((x) => x.id === selectedId), [plans, selectedId]);
  const housePrice = selectedPlan?.base_price || 0;
  const land = Number(landPrice) || 0;
  const total = housePrice + land;

  const pickPlan = (p) => {
    setSelectedId(p.id);
    if (!(data.rewards || []).some((r) => r.name === INVESTOR_OFFER_1.name)) {
      addReward(INVESTOR_OFFER_1);
      setShowReward(true);
    }
  };

  const saveNext = () => {
    const p = selectedPlan;
    if (!p) { toast.error("Select a floor plan to continue."); return; }
    if (land <= 0) { toast.error("Enter an estimated land price to build a realistic house-and-land total."); return; }
    const investor_floorplan = {
      id: p.id, name: p.name, base_price: p.base_price, beds: p.beds, baths: p.baths,
      garage: p.garage, storeys: p.storeys, house_size_m2: p.house_size_m2,
      image_url: p.image_url, floorplan_image_url: p.floorplan_image_url,
      house_price: Math.round(housePrice), land_price: Math.round(land), total_price: Math.round(total),
    };
    mergeData({ investor_floorplan });
    if (session) api.patchSession(session.id, { data: { investor_floorplan } }).catch(() => {});
    next();
  };

  return (
    <StageFrame testId="stage-investor-floorplan" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Planned around your tenants</Eyebrow>
        <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3">
          Choose a rental-ready design{loc.suburb ? ` for ${loc.suburb}` : ""}.
        </h1>
        <p className="text-mutedText mt-3 max-w-2xl">Low-maintenance, high-appeal homes that attract quality tenants and strong rents.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="investor-floorplan-grid">
          {plans.map((p, i) => {
            const sel = selectedId === p.id;
            const img = p.floorplan_image_url || p.image_url;
            return (
              <motion.button key={p.id} data-testid={`floorplan-${i}`} onClick={() => pickPlan(p)}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
                className={`text-left rounded-2xl border overflow-hidden transition ${sel ? "border-brand ring-2 ring-brand/40" : "border-line hover:border-brand/40"}`}>
                <div className="relative h-52 bg-white grid place-items-center p-3">
                  {img
                    ? <img src={img} alt={`${p.name} floor plan`} className="w-full h-full object-contain" />
                    : <span className="text-mutedText text-sm">Floor plan coming soon</span>}
                  {sel && <div className="absolute top-3 right-3 grid place-items-center w-7 h-7 rounded-full bg-brand text-white"><CheckCircle2 size={16} /></div>}
                </div>
                <div className="p-4">
                  <div className="font-medium text-forest">{p.name}</div>
                  <div className="font-serif text-lg text-brand mt-0.5">{money(p.base_price)} <span className="text-xs text-mutedText font-sans">house</span></div>
                  <div className="flex items-center gap-4 text-mutedText text-sm mt-2">
                    <span className="flex items-center gap-1"><BedDouble size={14} />{p.beds}</span>
                    <span className="flex items-center gap-1"><Bath size={14} />{p.baths}</span>
                    <span className="flex items-center gap-1"><Car size={14} />{p.garage || 0}</span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {selectedPlan && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
            className="mt-8 rounded-2xl border border-line bg-white/[0.03] p-6" data-testid="investor-land-panel">
            <div className="flex items-center gap-2 text-brand"><MapPin size={18} /><span className="label-eyebrow text-forest/70">Estimated land price{loc.suburb ? ` in ${loc.suburb}` : ""}</span></div>
            <p className="text-mutedText text-sm mt-1.5">Add a realistic land price to build a true house-and-land total for this design.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
              <div>
                <label className="label-eyebrow text-mutedText">Estimated land price</label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mutedText">$</span>
                  <input data-testid="investor-land-price" type="number" min="0" step="1000" value={landPrice}
                    onChange={(e) => setLandPrice(e.target.value)} placeholder="e.g. 320,000"
                    className="w-full rounded-xl border border-line bg-white/5 pl-7 pr-3 py-3 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
              </div>
              <div className="rounded-2xl border border-line bg-white/5 p-5" data-testid="investor-total-summary">
                <div className="flex justify-between text-sm"><span className="text-mutedText">House ({selectedPlan.name})</span><span className="text-forest font-medium">{money(housePrice)}</span></div>
                <div className="flex justify-between text-sm mt-2"><span className="text-mutedText">Land (estimated)</span><span className="text-forest font-medium">{money(land)}</span></div>
                <div className="flex justify-between border-t border-line pt-3 mt-3"><span className="text-forest">House &amp; land total</span><span className="text-brand font-serif text-xl" data-testid="investor-house-land-total">{money(total)}</span></div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <RewardPopup open={showReward} onClose={() => setShowReward(false)} title={INVESTOR_OFFER_1.name} value={INVESTOR_OFFER_1.value} subtitle="Yours when you build this investment with us." />
      <ConsultantNav testId="stage-investor-floorplan" onBack={back} onNext={saveNext} nextLabel="Work out the funding" />
    </StageFrame>
  );
}
